import { Injectable } from '@angular/core';
import { IBaseElement, IBaseElementPayload } from '../interfaces';
import { CreatorStateService } from '../services/app-services';
import { FlattenSeat, GuidWrapper } from '../types';
import { TextFormattingUtils } from '../utils';
import { ShapeKeys } from '../enums';
import { ShapeAttributesManager } from './shape-attributes.manager';
import { Guid } from 'guid-typescript';
import { DEFAULT_BASE_ELEMENT_DATA } from '../constants';
import { HTMLElementUtils } from '@cinetixx/cinetixx-ui';

@Injectable()
export class BaseElementManager {

  public static transformHTMLElementToObject(el: HTMLElement): GuidWrapper<IBaseElement> {
    const baseElement = CreatorStateService.seatsPlan.baseElements.find(
      (bEl) => bEl.guid === ShapeAttributesManager.getGUID(el)
    );

    const elData = {
      webX: parseInt(el.style.left),
      webY: parseInt(el.style.top),
      w: el.style.width ? parseInt(el.style.width) : parseInt(getComputedStyle(el).width),
      h: el.style.height ? parseInt(el.style.height) : parseInt(getComputedStyle(el).height),
      color: ShapeAttributesManager.getShapeBackgroundColor(el),
      shapeId: ShapeAttributesManager.getShapeId(el),
      rotate: HTMLElementUtils.getRotationDegrees(el.querySelector('.shape .editable-clip-path')),
      guid: ShapeAttributesManager.getGUID(el)
    } as GuidWrapper<IBaseElement>;

    if (baseElement) {
      return BaseElementManager.formatObject({
        ...baseElement,
        ...elData,
      });
    }

    return BaseElementManager.formatObject(elData);
  }

  public static getHTMLElement(guid: string, container?: HTMLElement): HTMLElement {
    return (container ?? CreatorStateService.roomRef.nativeElement).querySelector<HTMLElement>(
      `[${ShapeAttributesManager.getAttribute(ShapeKeys.GUID)}="${guid}"]`
    ) || null;
  }

  public static getBaseElement(guid: string): GuidWrapper<IBaseElement> {
    return CreatorStateService.seatsPlan.baseElements.find(x => x.guid === guid);
  }

  public static getDataToSend(baseElement: Partial<IBaseElement>): IBaseElementPayload {
    const shape = CreatorStateService.shapes.find(x => x.id === baseElement.shapeId);

    return {
      ...BaseElementManager.getBaseObject(baseElement),
      width: baseElement?.w ?? DEFAULT_BASE_ELEMENT_DATA.w,
      height: baseElement?.h ?? DEFAULT_BASE_ELEMENT_DATA.h,
      title: TextFormattingUtils.formatText(baseElement?.ruleTitle, baseElement as FlattenSeat) || baseElement.ruleTitle || DEFAULT_BASE_ELEMENT_DATA.title,
      image: baseElement.image ?? shape.image,
      imageX: baseElement.imageX ?? shape.imageX,
      imageY: baseElement.imageY ?? shape.imageY,
      sectorElementId: baseElement?.id ?? null,
      bottomText: TextFormattingUtils.formatText(baseElement?.ruleBottomText, baseElement as FlattenSeat) || DEFAULT_BASE_ELEMENT_DATA.bottomText,
      tipText: TextFormattingUtils.formatText(baseElement?.ruleTipText, baseElement as FlattenSeat) || DEFAULT_BASE_ELEMENT_DATA.tipText,
      topText: TextFormattingUtils.formatText(baseElement?.ruleTopText, baseElement as FlattenSeat) || DEFAULT_BASE_ELEMENT_DATA.topText,
    } as IBaseElementPayload;
  }

  public static formatObject(el: Partial<GuidWrapper<IBaseElement>>): GuidWrapper<IBaseElement> {
    return {
      ...BaseElementManager.getBaseObject(el),
      w: el?.w ?? DEFAULT_BASE_ELEMENT_DATA.w,
      h: el?.h ?? DEFAULT_BASE_ELEMENT_DATA.h,
      guid: el.guid ?? Guid.raw(),
      title: el.title ?? DEFAULT_BASE_ELEMENT_DATA.title,
      image: el.image ?? DEFAULT_BASE_ELEMENT_DATA.image,
      imageX: el.imageX ?? DEFAULT_BASE_ELEMENT_DATA.imageX,
      imageY: el.imageY ?? DEFAULT_BASE_ELEMENT_DATA.imageY,
      id: el.id ?? DEFAULT_BASE_ELEMENT_DATA.id,
      topText: el.topText ?? DEFAULT_BASE_ELEMENT_DATA.topText,
      bottomText: el.bottomText ?? DEFAULT_BASE_ELEMENT_DATA.bottomText,
      tipText: el.tipText ?? DEFAULT_BASE_ELEMENT_DATA.tipText,
    } as GuidWrapper<IBaseElement>;
  }

  private static getBaseObject(el: Partial<GuidWrapper<IBaseElement>>): Partial<GuidWrapper<IBaseElement>> {
    return {
      webX: el?.webX ?? DEFAULT_BASE_ELEMENT_DATA.webX,
      webY: el?.webY ?? DEFAULT_BASE_ELEMENT_DATA.webY,
      x: el?.x ?? DEFAULT_BASE_ELEMENT_DATA.x,
      y: el?.y ?? DEFAULT_BASE_ELEMENT_DATA.y,
      color: el?.color ?? DEFAULT_BASE_ELEMENT_DATA.color,
      flagRankFac: DEFAULT_BASE_ELEMENT_DATA.flagRankFac,
      flagSeatFac: DEFAULT_BASE_ELEMENT_DATA.flagSeatFac,
      titleSizeMin: el?.titleSizeMin ?? DEFAULT_BASE_ELEMENT_DATA.titleSizeMin,
      titleSizeMax: el?.titleSizeMax ?? DEFAULT_BASE_ELEMENT_DATA.titleSizeMax,
      shapeId: el?.shapeId || DEFAULT_BASE_ELEMENT_DATA.shapeId,
      rotate: el?.rotate ?? DEFAULT_BASE_ELEMENT_DATA.rotate,
      alignCurveId: el?.alignCurveId ?? DEFAULT_BASE_ELEMENT_DATA.alignCurveId,
      alignCurvePosition: el?.alignCurvePosition ?? DEFAULT_BASE_ELEMENT_DATA.alignCurvePosition,
      seatConfigurationId: el?.seatConfigurationId ?? CreatorStateService.seatsPlan.seatConfigurationId,
      ruleTitle: el?.ruleTitle || DEFAULT_BASE_ELEMENT_DATA.ruleTitle,
      ruleTopText: el?.ruleTopText || DEFAULT_BASE_ELEMENT_DATA.ruleTopText,
      ruleTipText: el?.ruleTipText ?? DEFAULT_BASE_ELEMENT_DATA.ruleTipText,
      ruleBottomText: el?.ruleBottomText || DEFAULT_BASE_ELEMENT_DATA.ruleBottomText,
      sectorId: el?.sectorId ?? DEFAULT_BASE_ELEMENT_DATA.sectorId,
    };
  }
}
